<template>
    <transition name="footer-animation" appear>
        <div
        v-if="showFooter" class="footer-content-wrapper"
        :class="{
            'mobile': getMobileAppState,
            'apply-divider-style': footerWithInfoDisplayer,
            'mobile-status-bar-android': getDeviceInUse === 'android',
            'mobile-status-bar-ios': getDeviceInUse === 'ios'
        }"
        :style="{'padding-bottom': getDeviceInUse === 'android' ? getKeyboardHeight : '' }">
            <slot name="footer-info-displayer"></slot>
            <div class="footer-wrapper" :class="{ mobile: getMobileAppState, 'apply-divider-style': footerWithInfoDisplayer }">
                <!-- Buttons Container -->
                <div
                v-if="!buttonFullWidth"
                class="buttons-container"
                :class="{
                    'only-right-buttons': !leftButtonsParams.length,
                    'buttons-with-half-size': buttonsParams.length === 2 && getMobileAppState && !usedInDetails
                }">
                    <!-- Left Buttons -->
                    <div v-if="leftButtonsParams.length" class="left-buttons-container">
                        <component
                        v-for="(button, index) in leftButtonsParams"
                        class="footer-button"
                        :key="index"
                        :is="buttonComponent(button.buttonType || 'tertiary')"
                        :label="button.label"
                        :icon="button.icon"
                        :iconPosition="button.iconPosition"
                        :buttonSize="!getMobileAppState ? 'medium' : 'small'"
                        :buttonVariant="button.buttonVariant"
                        :disabled="button.disabled"
                        @clicked="button.callbackFunction" />
                    </div>
                    <!-- Right Buttons -->
                    <div class="right-buttons-container">
                        <component
                        v-for="(button, index) in rightButtonsParams"
                        class="footer-button"
                        :key="index"
                        :is="buttonComponent(button.buttonType || 'tertiary')"
                        :label="button.label"
                        :icon="button.icon"
                        :iconPosition="button.iconPosition"
                        :buttonVariant="button.buttonVariant"
                        :buttonSize="!getMobileAppState ? 'medium' : 'small'"
                        :disabled="button.disabled"
                        @clicked="button.callbackFunction" />
                    </div>
                </div>
                <!-- Single Action -->
                <div v-else class="single-action-button-container">
                    <component
                    v-for="(button, index) in buttonsParams"
                    class="single-action-button"
                    :key="index"
                    :is="buttonComponent(button.buttonType || 'tertiary')"
                    :label="button.label"
                    :icon="button.icon"
                    :iconPosition="button.iconPosition"
                    :buttonVariant="button.buttonVariant"
                    :buttonSize="!getMobileAppState ? 'medium' : 'small'"
                    :disabled="button.disabled"
                    @clicked="button.callbackFunction" />
                </div>
            </div>
        </div>
    </transition>
</template>

<script setup>
import ButtonPrimary from '@/components/generic/buttons/Primary'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import ButtonNegative from '@/components/generic/buttons/Negative'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'
/* Vue */
import { computed } from 'vue'
/* Emmitters */
const emit = defineEmits(['clicked'])
/* mapGetters */
const { getMobileAppState, getKeyboardHeight, getDeviceInUse } = mapGetters()

/** Props received:
@buttonsParams { Array } each item is an Object
    - buttonType { String } primary or tertiary
    - buttonPosition { String } left or right
    - buttonVariant {String} can be [full (default), only-border]
    - label { String }
    - icon { String }
    - iconPosition {String} can be [left (default), right]
    - disabled { Boolean }
    - callbackFunction { Function }
@showFooter { Boolean }
@buttonFullWidth { Boolean }
@footerWithInfoDisplayer { Boolean }
@usedInDetails { Boolean }
*/
const props = defineProps({
    buttonsParams: Array,
    showFooter: Boolean,
    buttonFullWidth: Boolean,
    footerWithInfoDisplayer: Boolean,
    usedInDetails: Boolean
})

/* list with type of buttons used in the footer */
const buttonsComponentsMapping = {
    'button-tertiary': ButtonTertiary,
    'button-primary': ButtonPrimary,
    'button-negative': ButtonNegative
}

const buttonComponent = (type) => {
    return buttonsComponentsMapping[`button-${type}`]
}

const leftButtonsParams = computed(() => {
    return props.buttonsParams.filter(button => button.buttonPosition === 'left')
})

const rightButtonsParams = computed(() => {
    return props.buttonsParams.filter(button => !button.buttonPosition || button.buttonPosition === 'right')
})
</script>

<style lang="scss" scoped>
.footer-content-wrapper {
    width: 100%;
    height: auto;

	padding-bottom: constant(safe-area-inset-bottom); /* iOS 11.0 */
	padding-bottom: env(safe-area-inset-bottom); /* iOS 11.2 */

    position: absolute;
    bottom: 0;
    left: 0;

    display: flex;
    flex-direction: column;

    background-color: var(--secondary-light-4);
    box-shadow: var(--box-shadow-1);

    transition: transform 1s ease;
    transform: translateY(0px);
    z-index: 1;

    &.footer-animation-enter-active,
    &.footer-animation-leave-active {
        transform: translateY(0);
    }

    &.footer-animation-enter-from,
    &.footer-animation-leave-to {
        transform: translateY(170%);
    }

    &.mobile {
        &.apply-divider-style {
            border-radius: 16px 16px 0 0;
        }

        &.mobile-status-bar-ios {
            padding-bottom: constant(safe-area-inset-bottom); /* iOS 11.0 */
            padding-bottom: env(safe-area-inset-bottom); /* iOS 11.2 */
        }
    }

    .footer-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 80px;

        padding: 16px;

        &.apply-divider-style {
            border-top: 1px solid var(--secondary-light-3);
        }

        .buttons-container {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;

            width: 100%;
            max-width: 508px;

            .left-buttons-container,
            .right-buttons-container {
                width: fit-content;
                display: flex;
                gap: 16px;
            }

            .right-buttons-container {
                justify-content: flex-end;
            }

            &.only-right-buttons {
                justify-content: flex-end;
            }
        }

        .single-action-button-container {
            width: 100%;

            display: flex;
            justify-content: center;

            .single-action-button {
                width: 100%;
                max-width: 508px;
            }
        }

        &.mobile {
            height: 72px;

            .left-buttons-container,
            .right-buttons-container {
                .button-primary, .tertiary-button-wrapper {
                    width: fit-content;
                    flex-shrink: 1;
                }
            }

            .buttons-with-half-size {
                .left-buttons-container,
                .right-buttons-container {
                    width: 50%;

                    .button-primary, .tertiary-button-wrapper, .negative-button-wrapper {
                        width: 100%;
                    }
                }
            }

            .only-right-buttons {
                .right-buttons-container {
                    width: 100%;

                    .button-primary, .tertiary-button-wrapper, .negative-button-wrapper {
                        width: 50%;
                    }
                }
            }
        }
    }
}
</style>
